import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function Features() {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);

  const features = [
    {
      icon: 'ri-robot-line',
      title: 'AI-Powered Trading Engine',
      description: 'Revolutionary machine learning algorithms analyze global market patterns and execute precision trades automatically for maximum profit optimization.',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-500/10 to-pink-500/10',
      link: '/robot'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Military-Grade Security',
      description: 'Advanced multi-layer security protocols, quantum-resistant encryption, and institutional-grade cold storage protect your digital assets.',
      gradient: 'from-cyan-500 to-blue-500',
      bgGradient: 'from-cyan-500/10 to-blue-500/10',
      link: '/wallet'
    },
    {
      icon: 'ri-exchange-line',
      title: 'Advanced P2P Trading',
      description: 'Direct peer-to-peer trading with smart contract escrow protection, competitive rates, and instant settlement capabilities.',
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-500/10 to-emerald-500/10',
      link: '/p2p'
    },
    {
      icon: 'ri-coins-line',
      title: 'Intelligent Mining',
      description: 'Next-generation mining algorithms optimize hardware performance and energy consumption for maximum efficiency and profitability.',
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-500/10 to-red-500/10',
      link: '/mining'
    },
    {
      icon: 'ri-wallet-3-line',
      title: 'Professional Wallet Suite',
      description: 'Secure multi-chain wallet supporting 100+ cryptocurrencies with advanced portfolio management and seamless cross-chain swapping.',
      gradient: 'from-indigo-500 to-purple-500',
      bgGradient: 'from-indigo-500/10 to-purple-500/10',
      link: '/wallet'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Professional Analytics',
      description: 'Institutional-grade trading tools with real-time market data, advanced technical indicators, and comprehensive market analysis.',
      gradient: 'from-pink-500 to-rose-500',
      bgGradient: 'from-pink-500/10 to-rose-500/10',
      link: '/trading'
    }
  ];

  const handleWatchDemo = () => {
    setShowDemo(true);
  };

  const handleFeatureClick = (link: string) => {
    navigate(link);
  };

  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements - Mobile Optimized */}
      <div className="absolute inset-0">
        <div className="absolute top-16 left-8 sm:top-20 sm:left-20 w-48 h-48 sm:w-72 sm:h-72 bg-purple-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-16 right-8 sm:bottom-20 sm:right-20 w-64 h-64 sm:w-96 sm:h-96 bg-cyan-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 sm:w-64 sm:h-64 bg-pink-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Platform Infrastructure & Risk Management
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Technical features designed for transparency, security, and regulatory compliance in decentralized finance operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: 'ri-shield-check-line',
              title: 'Multi-Layer Security Protocol',
              description: 'Enterprise-grade encryption, multi-signature wallets, and continuous security audits. Cold storage for majority of assets with real-time threat monitoring.',
              gradient: 'from-blue-500 to-cyan-500'
            },
            {
              icon: 'ri-line-chart-line',
              title: 'Transparent Risk Analytics',
              description: 'Real-time portfolio risk assessment, volatility indicators, and historical performance data. All metrics independently verifiable on-chain.',
              gradient: 'from-purple-500 to-pink-500'
            },
            {
              icon: 'ri-file-shield-2-line',
              title: 'Regulatory Compliance Framework',
              description: 'KYC/AML verification, transaction monitoring, and regulatory reporting systems. Designed to meet evolving financial regulations.',
              gradient: 'from-emerald-500 to-teal-500'
            },
            {
              icon: 'ri-exchange-line',
              title: 'Decentralized Trading Engine',
              description: 'Non-custodial peer-to-peer trading with automated market makers. Smart contract execution with transparent fee structure and slippage protection.',
              gradient: 'from-orange-500 to-red-500'
            },
            {
              icon: 'ri-database-2-line',
              title: 'Blockchain Data Verification',
              description: 'All transactions recorded on immutable distributed ledger. Independent verification available through blockchain explorers and audit trails.',
              gradient: 'from-indigo-500 to-purple-500'
            },
            {
              icon: 'ri-customer-service-2-line',
              title: 'Technical Support & Documentation',
              description: 'Comprehensive API documentation, developer resources, and dedicated support team. Educational materials on risk management and platform operations.',
              gradient: 'from-pink-500 to-rose-500'
            }
          ].map((feature, index) => (
            <div 
              key={index} 
              onClick={() => handleFeatureClick(feature.link)}
              className="group relative bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-8 border border-gray-200/50 hover:border-gray-300/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/10 cursor-pointer"
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgGradient} rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Icon - Mobile Optimized */}
                <div className="relative mb-4 sm:mb-6">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${feature.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <i className={`${feature.icon} text-lg sm:text-2xl text-white`}></i>
                  </div>
                  <div className={`absolute -inset-1 bg-gradient-to-r ${feature.gradient} rounded-xl sm:rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                </div>

                {/* Title - Mobile Optimized */}
                <h3 className="text-lg sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 group-hover:text-gray-900 transition-colors duration-300">
                  {feature.title}
                </h3>

                {/* Description - Mobile Optimized */}
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {feature.description}
                </p>

                {/* Learn More Link - Mobile Optimized */}
                <div className="mt-4 sm:mt-6 flex items-center text-purple-600 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <span className="mr-2 text-sm sm:text-base">Learn More</span>
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </div>
              </div>

              {/* Hover Border Effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.gradient} rounded-2xl sm:rounded-3xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Demo Modal - Mobile Optimized */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            <button
              onClick={() => setShowDemo(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-8 h-8 sm:w-10 sm:h-10 bg-black/20 hover:bg-black/40 rounded-full flex items-center justify-center text-white transition-colors duration-200"
            >
              <i className="ri-close-line text-lg sm:text-xl"></i>
            </button>
            
            <div className="p-4 sm:p-8">
              <h3 className="text-xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">
                PepCoin Platform Demo
              </h3>
              <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8 px-4 sm:px-0">
                Experience the future of cryptocurrency trading with our revolutionary platform demonstration
              </p>
              
              <div className="aspect-video bg-gradient-to-br from-purple-100 to-cyan-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <div className="text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <i className="ri-play-fill text-2xl sm:text-3xl text-white"></i>
                  </div>
                  <p className="text-gray-600 font-semibold text-sm sm:text-base">Interactive Demo Coming Soon</p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-2">Experience live trading simulation</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <Link
                  to="/trading"
                  className="p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg sm:rounded-xl text-center hover:scale-105 transition-transform duration-200"
                >
                  <i className="ri-line-chart-line text-xl sm:text-2xl text-purple-600 mb-2 block"></i>
                  <span className="font-semibold text-gray-800 text-sm sm:text-base">Live Trading</span>
                </Link>
                <Link
                  to="/wallet"
                  className="p-3 sm:p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg sm:rounded-xl text-center hover:scale-105 transition-transform duration-200"
                >
                  <i className="ri-wallet-3-line text-xl sm:text-2xl text-cyan-600 mb-2 block"></i>
                  <span className="font-semibold text-gray-800 text-sm sm:text-base">Wallet</span>
                </Link>
                <Link
                  to="/mining"
                  className="p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl text-center hover:scale-105 transition-transform duration-200"
                >
                  <i className="ri-coins-line text-xl sm:text-2xl text-green-600 mb-2 block"></i>
                  <span className="font-semibold text-gray-800 text-sm sm:text-base">Mining</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
